import { Link, createFileRoute } from '@tanstack/react-router';
import { ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { DateInput } from '@/components/date-input';
import { CurrencySettingsSection } from '@/components/currency-settings-section';
import { EInvoicingSettingsSection } from '@/components/einvoicing/einvoicing-settings-section';
import { FeaturesSection } from '@/components/portal/features-section';
import { AiAccountantSettingsSection } from '@/components/ai-accountant/ai-settings-section';
import { GstRegistrationSection } from '@/components/gst/gst-settings-section';
import { Checkbox, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { TextLink } from '@/components/menu-link';
import { SettingsSection } from '@/components/settings-section';
import { toast } from '@/components/toast';
import { Input } from '@/components/ui/input';
import { formatTrn, useMe } from '@/lib/clients';
import { displayDate, utcOffsetLabel } from '@/lib/dates';
import { stateLabel } from '@/lib/gst';
import { useOrgId } from '@/lib/org';
import {
  EMIRATES,
  MONTHS,
  TERMS,
  termsLabel,
  useCompanySettings,
  useSaveSettings,
  type CompanySettings,
  type SettingsPatch,
} from '@/lib/settings';
import { cn } from '@/lib/utils';

const TABS = [
  { key: 'company', label: 'Company' },
  { key: 'sales', label: 'Sales' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'advanced', label: 'Advanced' },
] as const;
type Tab = (typeof TABS)[number]['key'];

export const Route = createFileRoute('/settings/company')({
  component: CompanySettingsPage,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    TABS.some((t) => t.key === s['tab']) ? { tab: s['tab'] as Tab } : {},
});

const TEMPLATE_LABELS: Record<string, string> = {
  trading: 'Trading (goods)',
  services: 'Services',
  free_zone: 'Free zone company',
  india_trading: 'Trading (goods), India',
  india_services: 'Services, India',
};
const yesNo = (b: boolean) => (b ? 'On' : 'Off');

/** Screen G1 (document 06): left tabs, sections with a read view and an inline pencil. */
function CompanySettingsPage() {
  const orgId = useOrgId();
  const { tab = 'company' } = Route.useSearch();
  const settings = useCompanySettings();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;

  return (
    <>
      <PageHeader
        title="Company settings"
        description={settings.data ? settings.data.company.name : undefined}
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : settings.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : settings.isError ? (
        <EmptyState title="Could not load settings" hint={settings.error.message} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_minmax(0,1fr)] md:gap-6">
          {/* a scrolling row of tabs on a phone, a side list from `md` */}
          <nav
            aria-label="Settings sections"
            className="-mx-4 flex gap-1 overflow-x-auto border-b px-4 md:mx-0 md:flex-col md:border-b-0 md:px-0"
          >
            {TABS.map((t) => (
              <Link
                key={t.key}
                to="/settings/company"
                search={{ tab: t.key }}
                className={cn(
                  'shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm md:rounded-md md:border-b-0 md:border-l-2',
                  tab === t.key
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'border-transparent text-muted-foreground hover:bg-muted',
                )}
              >
                {t.label}
              </Link>
            ))}
          </nav>
          <div className="flex min-w-0 max-w-4xl flex-col gap-4">
            {!canEdit ? (
              <p className="text-sm text-muted-foreground">
                Your accountant maintains these settings. Contact them to change anything.
              </p>
            ) : null}
            {tab === 'company' ? <CompanyTab s={settings.data} canEdit={canEdit} /> : null}
            {tab === 'sales' ? <SalesTab s={settings.data} canEdit={canEdit} /> : null}
            {tab === 'expenses' ? <ExpensesTab s={settings.data} canEdit={canEdit} /> : null}
            {tab === 'advanced' ? <AdvancedTab s={settings.data} canEdit={canEdit} /> : null}
          </div>
        </div>
      )}
    </>
  );
}

/** Save a patch and toast; field errors come back from the server through SettingsSection. */
function useSave() {
  const save = useSaveSettings();
  return async (patch: SettingsPatch, what: string) => {
    await save.mutateAsync(patch);
    toast(`${what} saved.`);
  };
}

type Draft = Record<string, string | boolean>;
function useDraft(initial: () => Draft) {
  const [d, setD] = useState<Draft>({});
  return {
    d,
    load: () => setD(initial()),
    str: (k: string) => String(d[k] ?? ''),
    bool: (k: string) => Boolean(d[k]),
    set: (k: string) => (v: string | boolean) => setD((x) => ({ ...x, [k]: v })),
  };
}

function CompanyTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const c = s.company;
  const name = useDraft(() => ({ name: c.name, legalName: c.legalName ?? '' }));
  const contact = useDraft(() => ({
    email: c.email ?? '',
    phone: c.phone ?? '',
    website: c.website ?? '',
  }));
  const addr = useDraft(() => ({
    line1: c.address.line1 ?? '',
    line2: c.address.line2 ?? '',
    city: c.address.city ?? '',
    poBox: c.address.poBox ?? '',
    postalCode: c.address.postalCode ?? '',
    emirate: c.emirate ?? '',
  }));
  // India clients: PIN code and GST state instead of P.O. Box and emirate
  const india = Boolean(s.gst);
  const vat = useDraft(() => ({
    vatRegistered: s.vat.vatRegistered,
    trn: s.vat.trn ? formatTrn(s.vat.trn) : '',
    vatRegistrationDate: s.vat.vatRegistrationDate ?? '',
    taxPeriod: s.vat.taxPeriod,
  }));
  const address = (
    india
      ? [
          c.address.line1,
          c.address.line2,
          [c.address.city, c.address.postalCode].filter(Boolean).join(' '),
          `${stateLabel(s.gst?.stateCode)}, India`,
        ]
      : [
          c.address.line1,
          c.address.line2,
          [...new Set([c.address.city, c.emirate].filter(Boolean))].join(', '),
          c.address.poBox ? `P.O. Box ${c.address.poBox}` : null,
          'United Arab Emirates',
        ]
  )
    .filter(Boolean)
    .join('\n');

  return (
    <>
      <SettingsSection
        title="Company name"
        testId="section-company-name"
        canEdit={canEdit}
        onEdit={name.load}
        rows={[
          ['Company name', c.name],
          ['Legal name', c.legalName],
          ['Logo', <LogoPlaceholder key="logo" />],
        ]}
        onSave={async () => {
          if (name.str('name').trim().length < 2) return { name: 'Enter the company name.' };
          await save(
            { company: { name: name.str('name'), legalName: name.str('legalName') } },
            'Company name',
          );
        }}
      >
        {(errors) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Company name"
              hint="Shown in the app and on documents."
              error={errors['name']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={name.str('name')}
                  aria-invalid={Boolean(errors['name'])}
                  onChange={(e) => name.set('name')(e.target.value)}
                  autoFocus
                />
              )}
            </Field>
            <Field label="Legal name" hint="As on the trade licence.">
              {(id) => (
                <Input
                  id={id}
                  value={name.str('legalName')}
                  onChange={(e) => name.set('legalName')(e.target.value)}
                />
              )}
            </Field>
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title="Contact info"
        testId="section-contact"
        canEdit={canEdit}
        onEdit={contact.load}
        rows={[
          ['Company email', c.email],
          ['Company phone', c.phone],
          ['Website', c.website],
        ]}
        onSave={async () => {
          const email = contact.str('email').trim();
          if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email))
            return { email: 'Enter a valid email address.' };
          await save(
            {
              company: {
                email,
                phone: contact.str('phone'),
                website: contact.str('website'),
              },
            },
            'Contact info',
          );
        }}
      >
        {(errors) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Company email" error={errors['email']}>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={contact.str('email')}
                  aria-invalid={Boolean(errors['email'])}
                  onChange={(e) => contact.set('email')(e.target.value)}
                />
              )}
            </Field>
            <Field label="Company phone">
              {(id) => (
                <Input
                  id={id}
                  value={contact.str('phone')}
                  onChange={(e) => contact.set('phone')(e.target.value)}
                />
              )}
            </Field>
            <Field label="Website">
              {(id) => (
                <Input
                  id={id}
                  value={contact.str('website')}
                  onChange={(e) => contact.set('website')(e.target.value)}
                />
              )}
            </Field>
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title="Address"
        testId="section-address"
        canEdit={canEdit}
        onEdit={addr.load}
        rows={[['Company address', address]]}
        onSave={() =>
          save(
            {
              company: {
                address: {
                  line1: addr.str('line1'),
                  line2: addr.str('line2'),
                  city: addr.str('city'),
                  ...(india
                    ? { postalCode: addr.str('postalCode') }
                    : { poBox: addr.str('poBox') }),
                },
                ...(india ? {} : { emirate: addr.str('emirate') || null }),
              },
            },
            'Address',
          )
        }
      >
        {() => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Street address" className="col-span-2">
              {(id) => (
                <Input
                  id={id}
                  value={addr.str('line1')}
                  onChange={(e) => addr.set('line1')(e.target.value)}
                />
              )}
            </Field>
            <Field label="Address line 2" className="col-span-2">
              {(id) => (
                <Input
                  id={id}
                  value={addr.str('line2')}
                  onChange={(e) => addr.set('line2')(e.target.value)}
                />
              )}
            </Field>
            <Field label="City">
              {(id) => (
                <Input
                  id={id}
                  value={addr.str('city')}
                  onChange={(e) => addr.set('city')(e.target.value)}
                />
              )}
            </Field>
            {india ? (
              <>
                <Field label="PIN code">
                  {(id) => (
                    <Input
                      id={id}
                      value={addr.str('postalCode')}
                      inputMode="numeric"
                      onChange={(e) => addr.set('postalCode')(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="State" hint="Set with the GST registration.">
                  {(id) => <Input id={id} value={stateLabel(s.gst?.stateCode)} disabled />}
                </Field>
                <Field label="Country">{(id) => <Input id={id} value="India" disabled />}</Field>
              </>
            ) : null}
            {india ? null : (
              <Field label="P.O. Box">
                {(id) => (
                  <Input
                    id={id}
                    value={addr.str('poBox')}
                    onChange={(e) => addr.set('poBox')(e.target.value)}
                  />
                )}
              </Field>
            )}
            {india ? null : (
              <Field label="Emirate" hint="Default place of supply.">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={addr.str('emirate')}
                    onChange={(e) => addr.set('emirate')(e.target.value)}
                  >
                    <option value="">Not set</option>
                    {EMIRATES.map((em) => (
                      <option key={em}>{em}</option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            {india ? null : (
              <Field label="Country">
                {(id) => <Input id={id} value="United Arab Emirates" disabled />}
              </Field>
            )}
          </div>
        )}
      </SettingsSection>

      {india ? (
        <GstRegistrationSection s={s} canEdit={canEdit} />
      ) : (
        <SettingsSection
          title="VAT registration"
          testId="section-vat"
          canEdit={canEdit}
          onEdit={vat.load}
          rows={[
            ['VAT registered', s.vat.vatRegistered ? 'Yes' : 'No'],
            ['TRN', formatTrn(s.vat.trn)],
            [
              'Effective date of registration',
              s.vat.vatRegistrationDate ? displayDate(s.vat.vatRegistrationDate) : '',
            ],
            ['VAT filing period', s.vat.taxPeriod === 'monthly' ? 'Monthly' : 'Quarterly'],
            ['Tax pack', s.vat.taxPack === 'uae' ? 'UAE VAT (FTA)' : s.vat.taxPack],
          ]}
          onSave={async () => {
            const trn = vat.str('trn').replace(/\s+/g, '');
            if (trn && !/^[0-9]{15}$/.test(trn)) return { trn: 'A TRN has 15 digits.' };
            if (vat.bool('vatRegistered') && !trn)
              return { trn: 'A VAT-registered company needs its TRN.' };
            await save(
              {
                vat: {
                  vatRegistered: vat.bool('vatRegistered'),
                  trn: trn || null,
                  vatRegistrationDate: vat.str('vatRegistrationDate') || null,
                  taxPeriod: vat.str('taxPeriod'),
                },
              },
              'VAT registration',
            );
          }}
        >
          {(errors) => (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Checkbox
                className="col-span-2"
                label="Registered for VAT"
                checked={vat.bool('vatRegistered')}
                onChange={(e) => vat.set('vatRegistered')(e.target.checked)}
              />
              <Field label="TRN" hint="15 digits, from the FTA certificate." error={errors['trn']}>
                {(id) => (
                  <Input
                    id={id}
                    value={vat.str('trn')}
                    inputMode="numeric"
                    placeholder="100 1234567 00 003"
                    aria-invalid={Boolean(errors['trn'])}
                    onChange={(e) => vat.set('trn')(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Effective date of registration">
                {(id) => (
                  <DateInput
                    id={id}
                    value={vat.str('vatRegistrationDate')}
                    onChange={(v) => vat.set('vatRegistrationDate')(v)}
                  />
                )}
              </Field>
              <Field label="VAT filing period">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={vat.str('taxPeriod')}
                    onChange={(e) => vat.set('taxPeriod')(e.target.value)}
                  >
                    <option value="quarterly">Quarterly</option>
                    <option value="monthly">Monthly</option>
                  </NativeSelect>
                )}
              </Field>
            </div>
          )}
        </SettingsSection>
      )}
    </>
  );
}

function LogoPlaceholder() {
  return (
    <span className="inline-flex items-center gap-3">
      <span className="flex size-12 items-center justify-center rounded-md border border-dashed text-muted-foreground">
        <ImageIcon className="size-5" aria-hidden />
      </span>
      <span className="text-xs text-muted-foreground">Logo upload is coming soon.</span>
    </span>
  );
}

function TermsSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {TERMS.map((t) => (
        <option key={t.days} value={t.days}>
          {t.label}
        </option>
      ))}
    </NativeSelect>
  );
}

function SalesTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const form = useDraft(() => ({
    defaultTermsDays: String(s.sales.defaultTermsDays),
    invoicePrefix: s.sales.invoicePrefix,
    showSku: s.sales.showSku,
  }));
  const msg = useDraft(() => ({
    invoiceMessage: s.sales.invoiceMessage ?? '',
    statementMessage: s.sales.statementMessage ?? '',
  }));
  const mail = useDraft(() => ({
    emailSubject: s.sales.emailSubject,
    emailMessage: s.sales.emailMessage,
    emailCc: s.sales.emailCc ?? '',
  }));
  return (
    <>
      <SettingsSection
        title="Sales form content"
        testId="section-sales-form"
        canEdit={canEdit}
        onEdit={form.load}
        rows={[
          ['Preferred invoice terms', termsLabel(s.sales.defaultTermsDays)],
          ['Invoice number prefix', s.sales.invoicePrefix],
          ['Show SKU column', yesNo(s.sales.showSku)],
        ]}
        onSave={async () => {
          if (!/^[A-Za-z0-9-]{0,10}$/.test(form.str('invoicePrefix')))
            return { invoicePrefix: 'Up to 10 letters, digits or dashes.' };
          await save(
            {
              sales: {
                defaultTermsDays: Number(form.str('defaultTermsDays')),
                invoicePrefix: form.str('invoicePrefix'),
                showSku: form.bool('showSku'),
              },
            },
            'Sales form content',
          );
        }}
      >
        {(errors) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Preferred invoice terms">
              {(id) => (
                <TermsSelect
                  id={id}
                  value={form.str('defaultTermsDays')}
                  onChange={form.set('defaultTermsDays')}
                />
              )}
            </Field>
            <Field
              label="Invoice number prefix"
              hint="Invoice numbers are allocated when an invoice is approved."
              error={errors['invoicePrefix']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form.str('invoicePrefix')}
                  aria-invalid={Boolean(errors['invoicePrefix'])}
                  onChange={(e) => form.set('invoicePrefix')(e.target.value)}
                />
              )}
            </Field>
            <Checkbox
              label="Show SKU column on sales forms"
              checked={form.bool('showSku')}
              onChange={(e) => form.set('showSku')(e.target.checked)}
            />
          </div>
        )}
      </SettingsSection>
      <SettingsSection
        title="Messages"
        testId="section-messages"
        canEdit={canEdit}
        onEdit={msg.load}
        rows={[
          ['Default message on invoices', s.sales.invoiceMessage],
          ['Default message on statements', s.sales.statementMessage],
        ]}
        onSave={() =>
          save(
            {
              sales: {
                invoiceMessage: msg.str('invoiceMessage'),
                statementMessage: msg.str('statementMessage'),
              },
            },
            'Messages',
          )
        }
      >
        {() => (
          <>
            <Field label="Default message on invoices">
              {(id) => (
                <Textarea
                  id={id}
                  value={msg.str('invoiceMessage')}
                  onChange={(e) => msg.set('invoiceMessage')(e.target.value)}
                />
              )}
            </Field>
            <Field label="Default message on statements">
              {(id) => (
                <Textarea
                  id={id}
                  value={msg.str('statementMessage')}
                  onChange={(e) => msg.set('statementMessage')(e.target.value)}
                />
              )}
            </Field>
          </>
        )}
      </SettingsSection>
      <SettingsSection
        title="Email messages"
        testId="section-email-messages"
        canEdit={canEdit}
        onEdit={mail.load}
        rows={[
          ['Default email subject', s.sales.emailSubject],
          ['Default email message', s.sales.emailMessage],
          ['Copy (Cc) every sales email to', s.sales.emailCc],
        ]}
        onSave={async () => {
          const errors: Record<string, string> = {};
          if (!mail.str('emailSubject').trim()) errors['emailSubject'] = 'Enter a subject.';
          if (!mail.str('emailMessage').trim()) errors['emailMessage'] = 'Enter a message.';
          const cc = mail.str('emailCc').trim();
          if (cc && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cc))
            errors['emailCc'] = 'Enter a valid email address.';
          if (Object.keys(errors).length) return errors;
          await save(
            {
              sales: {
                emailSubject: mail.str('emailSubject'),
                emailMessage: mail.str('emailMessage'),
                emailCc: cc,
              },
            },
            'Email messages',
          );
        }}
      >
        {(errors) => (
          <>
            <p className="text-sm text-muted-foreground">
              Used when you send an invoice, credit note, sales receipt or quote. These words are
              replaced: {'{document}'}, {'{number}'}, {'{customer}'}, {'{company}'}, {'{amount}'}{' '}
              (the balance due on invoices) and {'{due date}'}.
            </p>
            <Field label="Default email subject" error={errors['emailSubject']}>
              {(id) => (
                <Input
                  id={id}
                  value={mail.str('emailSubject')}
                  maxLength={300}
                  aria-invalid={Boolean(errors['emailSubject'])}
                  onChange={(e) => mail.set('emailSubject')(e.target.value)}
                />
              )}
            </Field>
            <Field label="Default email message" error={errors['emailMessage']}>
              {(id) => (
                <Textarea
                  id={id}
                  className="min-h-40"
                  value={mail.str('emailMessage')}
                  maxLength={5000}
                  aria-invalid={Boolean(errors['emailMessage'])}
                  onChange={(e) => mail.set('emailMessage')(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Copy (Cc) every sales email to"
              hint="Optional, for example the client's accounts mailbox."
              error={errors['emailCc']}
            >
              {(id) => (
                <Input
                  id={id}
                  value={mail.str('emailCc')}
                  aria-invalid={Boolean(errors['emailCc'])}
                  onChange={(e) => mail.set('emailCc')(e.target.value)}
                />
              )}
            </Field>
          </>
        )}
      </SettingsSection>
      {s.gst ? null : <EInvoicingSettingsSection canEdit={canEdit} />}
    </>
  );
}

function ExpensesTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const form = useDraft(() => ({
    defaultBillTermsDays: String(s.expenses.defaultBillTermsDays),
    showItemsTable: s.expenses.showItemsTable,
  }));
  return (
    <SettingsSection
      title="Bills and expenses"
      testId="section-bills"
      canEdit={canEdit}
      onEdit={form.load}
      rows={[
        ['Default bill payment terms', termsLabel(s.expenses.defaultBillTermsDays)],
        ['Show Items table on expense and purchase forms', yesNo(s.expenses.showItemsTable)],
      ]}
      onSave={() =>
        save(
          {
            expenses: {
              defaultBillTermsDays: Number(form.str('defaultBillTermsDays')),
              showItemsTable: form.bool('showItemsTable'),
            },
          },
          'Bills and expenses',
        )
      }
    >
      {() => (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Default bill payment terms">
            {(id) => (
              <TermsSelect
                id={id}
                value={form.str('defaultBillTermsDays')}
                onChange={form.set('defaultBillTermsDays')}
              />
            )}
          </Field>
          <Checkbox
            label="Show Items table on expense and purchase forms"
            checked={form.bool('showItemsTable')}
            onChange={(e) => form.set('showItemsTable')(e.target.checked)}
          />
        </div>
      )}
    </SettingsSection>
  );
}

function AdvancedTab({ s, canEdit }: { s: CompanySettings; canEdit: boolean }) {
  const save = useSave();
  const a = s.advanced;
  const acct = useDraft(() => ({ fiscalYearStart: String(a.fiscalYearStart) }));
  const coa = useDraft(() => ({ enableAccountNumbers: a.enableAccountNumbers }));
  const other = useDraft(() => ({
    warnDuplicateInvoiceNumbers: a.warnDuplicateInvoiceNumbers,
    warnDuplicateBillNumbers: a.warnDuplicateBillNumbers,
  }));
  const cats = useDraft(() => ({
    trackClasses: a.trackClasses ?? 'off',
    trackLocations: a.trackLocations ?? false,
    locationLabel: a.locationLabel ?? 'Location',
    useProjects: a.useProjects ?? false,
  }));
  const CLASS_MODES: Record<string, string> = {
    off: 'Off',
    per_line: 'One to each row in transaction',
    per_transaction: 'One to entire transaction',
  };
  return (
    <>
      <SettingsSection
        title="Accounting"
        testId="section-accounting"
        canEdit={canEdit}
        onEdit={acct.load}
        rows={[
          ['First month of financial year', MONTHS[a.fiscalYearStart - 1]],
          [
            'Close the books',
            <span key="close">
              Lock months on the Periods and close page (Accounting ›{' '}
              <TextLink to="/accounting/periods">Periods and close</TextLink>
              ).
            </span>,
          ],
        ]}
        onSave={() =>
          save({ advanced: { fiscalYearStart: Number(acct.str('fiscalYearStart')) } }, 'Accounting')
        }
      >
        {() => (
          <Field
            label="First month of financial year"
            hint={
              a.canChangeFiscalYearStart
                ? 'Re-creates the financial years and their months. Only possible before the first transaction.'
                : 'Fixed: the books already have transactions.'
            }
          >
            {(id) => (
              <NativeSelect
                id={id}
                className="max-w-xs"
                value={acct.str('fiscalYearStart')}
                disabled={!a.canChangeFiscalYearStart}
                onChange={(e) => acct.set('fiscalYearStart')(e.target.value)}
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        )}
      </SettingsSection>
      <SettingsSection
        title="Chart of accounts"
        testId="section-coa"
        canEdit={canEdit}
        onEdit={coa.load}
        rows={[
          ['Enable account numbers', yesNo(a.enableAccountNumbers)],
          [
            'Template the chart started from',
            a.chartTemplate ? TEMPLATE_LABELS[a.chartTemplate] : '',
          ],
        ]}
        onSave={() =>
          save(
            { advanced: { enableAccountNumbers: coa.bool('enableAccountNumbers') } },
            'Chart of accounts',
          )
        }
      >
        {() => (
          <Checkbox
            label="Enable account numbers"
            checked={coa.bool('enableAccountNumbers')}
            onChange={(e) => coa.set('enableAccountNumbers')(e.target.checked)}
          />
        )}
      </SettingsSection>
      <SettingsSection
        title="Categories"
        testId="section-categories"
        canEdit={canEdit}
        onEdit={cats.load}
        rows={[
          ['Track classes', CLASS_MODES[a.trackClasses ?? 'off']],
          [
            'Track locations',
            a.trackLocations ? `On, labelled "${a.locationLabel ?? 'Location'}"` : 'Off',
          ],
          ['Projects', yesNo(a.useProjects ?? false)],
        ]}
        onSave={() =>
          save(
            {
              advanced: {
                trackClasses: cats.str('trackClasses'),
                trackLocations: cats.bool('trackLocations'),
                locationLabel: cats.str('locationLabel'),
                useProjects: cats.bool('useProjects'),
              },
            },
            'Categories',
          )
        }
      >
        {() => (
          <div className="flex flex-col gap-3">
            <Field label="Track classes" hint="Classes are kept at gear › Lists › Classes.">
              {(id) => (
                <NativeSelect
                  id={id}
                  className="max-w-xs"
                  value={cats.str('trackClasses')}
                  onChange={(e) => cats.set('trackClasses')(e.target.value)}
                >
                  {Object.entries(CLASS_MODES).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Checkbox
              label="Track locations (one to each transaction)"
              checked={cats.bool('trackLocations')}
              onChange={(e) => cats.set('trackLocations')(e.target.checked)}
            />
            {cats.bool('trackLocations') ? (
              <Field label="Location label">
                {(id) => (
                  <NativeSelect
                    id={id}
                    className="max-w-xs"
                    value={cats.str('locationLabel')}
                    onChange={(e) => cats.set('locationLabel')(e.target.value)}
                  >
                    {[
                      'Location',
                      'Business',
                      'Department',
                      'Division',
                      'Property',
                      'Store',
                      'Territory',
                    ].map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            ) : null}
            <Checkbox
              label="Use projects"
              checked={cats.bool('useProjects')}
              onChange={(e) => cats.set('useProjects')(e.target.checked)}
            />
          </div>
        )}
      </SettingsSection>
      <CurrencySettingsSection canEdit={canEdit} home={a.baseCurrency} />
      <SettingsSection
        title="Other preferences"
        testId="section-other"
        canEdit={canEdit}
        onEdit={other.load}
        rows={[
          ['Date format', a.dateFormat],
          ['Number format', '1,234.56'],
          [
            'Time zone',
            utcOffsetLabel(a.timeZone)
              ? `${a.timeZone} (${utcOffsetLabel(a.timeZone)})`
              : a.timeZone,
          ],
          ['Warn if duplicate invoice number is used', yesNo(a.warnDuplicateInvoiceNumbers)],
          ['Warn if duplicate bill number is used', yesNo(a.warnDuplicateBillNumbers)],
        ]}
        onSave={() =>
          save(
            {
              advanced: {
                warnDuplicateInvoiceNumbers: other.bool('warnDuplicateInvoiceNumbers'),
                warnDuplicateBillNumbers: other.bool('warnDuplicateBillNumbers'),
              },
            },
            'Other preferences',
          )
        }
      >
        {() => (
          <div className="flex flex-col gap-2">
            <Checkbox
              label="Warn if duplicate invoice number is used"
              checked={other.bool('warnDuplicateInvoiceNumbers')}
              onChange={(e) => other.set('warnDuplicateInvoiceNumbers')(e.target.checked)}
            />
            <Checkbox
              label="Warn if duplicate bill number is used"
              checked={other.bool('warnDuplicateBillNumbers')}
              onChange={(e) => other.set('warnDuplicateBillNumbers')(e.target.checked)}
            />
          </div>
        )}
      </SettingsSection>
      <FeaturesSection canEdit={canEdit} />
      <AiAccountantSettingsSection canEdit={canEdit} />
    </>
  );
}
